// bm-design-system: theme helper
import * as React from "react";

const STORAGE_KEY = "bm-ds-theme";

export type Theme = "light" | "dark" | "system";

// SOH GD Wines: the Private Cellar direction is dark-first. A visitor with no
// stored preference gets dark (mirrors the pre-paint script in the layout).
export const DEFAULT_THEME: Theme = "dark";

function getSystemPreference(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system")
      return stored;
  } catch {
    // Storage blocked (private mode, sandboxed iframe) — fall through.
  }
  return DEFAULT_THEME;
}

export function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  const resolved = theme === "system" ? getSystemPreference() : theme;
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

// Every mounted toggle subscribes here so they stay in sync with each other
// and with other tabs (via the `storage` event).
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      applyTheme(getStoredTheme());
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

// The server snapshot is the default so SSR markup matches the first client
// render during hydration; React then re-renders with the stored value.
const getServerSnapshot = () => DEFAULT_THEME;

export function useTheme() {
  const theme = React.useSyncExternalStore(
    subscribe,
    getStoredTheme,
    getServerSnapshot,
  );

  React.useEffect(() => {
    // Keep the <html> class consistent even if the inline boot script in the
    // HTML layout was bypassed.
    applyTheme(theme);
    if (theme !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = React.useCallback((next: Theme) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage blocked — still apply for this page view.
    }
    applyTheme(next);
    listeners.forEach((listener) => listener());
  }, []);

  return [theme, setTheme] as const;
}
